// The app's side of the knowledge base and release notes (ERP-66, ERP-68): its own articles, the
// release notes of this build, the help drawer the top bar's "?" opens, and "What's new".
import {
  articleFor,
  type HelpArticle,
  type HelpSection,
  loadHelp,
  type ReleaseNote,
  registerGeneralHelp,
  releaseNotes,
  t,
  unseenNotes,
  useAuth,
} from "@keel/core";
import { Icon, Markdown, Modal } from "@keel/ui";
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";

registerGeneralHelp(() => import("./help/index"));

/** The commit this build was made from (CI passes it in); `dev` locally. */
export const BUILD = import.meta.env.VITE_KEEL_BUILD || "dev";

/** Every release note in this build, newest first. */
export const RELEASES: ReleaseNote[] = releaseNotes(
  import.meta.glob("./releases/*.md", { query: "?raw", import: "default", eager: true }) as Record<
    string,
    string
  >,
);

/** The knowledge base for the account's modules; loads on first use. */
export function useHelp(): HelpSection[] | undefined {
  const { me } = useAuth();
  const [sections, setSections] = useState<HelpSection[]>();
  const key = (me.modules ?? []).join(",");
  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed by the module list
  useEffect(() => {
    let live = true;
    loadHelp(me.modules).then((s) => live && setSections(s));
    return () => {
      live = false;
    };
  }, [key]);
  return sections;
}

export const articleHref = (a: HelpArticle) => `/help/${a.module}/${a.slug}`;

/** The article for the page you're on, beside the page, so nothing typed is lost. */
export function HelpDrawer({ onClose }: { onClose: () => void }) {
  const { pathname } = useLocation();
  const sections = useHelp();
  const article = sections && articleFor(sections, pathname);
  useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);
  return (
    <>
      <button type="button" className="drawer-scrim" aria-label={t("Close")} onClick={onClose} />
      <aside className="drawer" aria-label={t("Help")}>
        <div className="drawer-head">
          <div>
            <h2>{article?.title ?? t("Help")}</h2>
            {article?.summary && (
              <p className="sub muted" style={{ textTransform: "none" }}>
                {article.summary}
              </p>
            )}
          </div>
          <button type="button" className="icon-btn" onClick={onClose} aria-label={t("Close")}>
            <Icon name="x" />
          </button>
        </div>
        <div className="drawer-body">
          {!sections ? (
            <p className="muted">{t("Loading…")}</p>
          ) : article ? (
            <Markdown source={article.body.replace(/^#\s+.*\n+/, "")} />
          ) : (
            <p className="muted">{t("There's no article for this page yet. Search the knowledge base:")}</p>
          )}
          <div className="row" style={{ gap: 14, marginTop: 20, flexWrap: "wrap" }}>
            {article && (
              <Link to={articleHref(article)} onClick={onClose}>
                {t("Open in the knowledge base")}
              </Link>
            )}
            <Link to="/help" onClick={onClose}>
              {t("All help articles")}
            </Link>
            <Link to="/whats-new" onClick={onClose}>
              {t("What's new")}
            </Link>
          </div>
        </div>
      </aside>
    </>
  );
}

// ── what's new ──

const seenKey = (user: string) => `keel.releases.seen.${user}`;
/** The ids this user has been shown, or null if they've never been shown any. */
const readSeen = (user: string): string[] | null => {
  try {
    const v = JSON.parse(localStorage.getItem(seenKey(user)) ?? "null");
    return Array.isArray(v) ? v.filter((x) => typeof x === "string") : null;
  } catch {
    return null;
  }
};
const writeSeen = (user: string, ids: string[]) => {
  try {
    localStorage.setItem(seenKey(user), JSON.stringify(ids));
  } catch {
    // Private window or blocked storage: the notes show again next time, nothing worse.
  }
};

/** Notes this user hasn't seen since the last deploy, and a way to mark them read. */
export function useUnseen() {
  const { me } = useAuth();
  const user = me.user?.id ?? me.actor;
  const [seen, setSeen] = useState(() => readSeen(user));
  const unseen = useMemo(() => unseenNotes(RELEASES, seen, me.modules), [seen, me.modules]);
  // Everything in this build counts as read: notes of modules they don't have included, so
  // turning a module on later doesn't bring back its whole history.
  const markSeen = () => {
    const ids = RELEASES.map((n) => n.id);
    writeSeen(user, ids);
    setSeen(ids);
  };
  return { unseen, markSeen };
}

export function ReleaseEntry({ n }: { n: ReleaseNote }) {
  return (
    <article className="release" id={n.id}>
      <header>
        <h3>{n.title}</h3>
        <span className="muted mono" style={{ fontSize: 12 }}>
          {n.date}
        </span>
        {n.tickets.map((k) => (
          <span key={k} className="pill mono" style={{ fontSize: 11 }}>
            {k}
          </span>
        ))}
      </header>
      <Markdown source={n.body} />
    </article>
  );
}

/** Shown once after a deploy brings something this user hasn't seen. */
export function WhatsNewPopup() {
  const { unseen, markSeen } = useUnseen();
  const [open, setOpen] = useState(true);
  if (!open || !unseen.length) return null;
  const close = () => {
    markSeen();
    setOpen(false);
  };
  return (
    <Modal
      title={t("What's new in Keel")}
      width={620}
      onClose={close}
      footer={
        <>
          <Link to="/whats-new" className="btn" onClick={close}>
            {t("All release notes")}
          </Link>
          <button type="button" className="btn btn-primary" onClick={close}>
            {t("Got it")}
          </button>
        </>
      }
    >
      <div style={{ margin: "-18px -20px", maxHeight: "60vh", overflowY: "auto" }}>
        {unseen.slice(0, 8).map((n) => (
          <ReleaseEntry key={n.id} n={n} />
        ))}
      </div>
      {unseen.length > 8 && (
        <p className="muted" style={{ marginTop: 26 }}>
          {t("…and {n} more in the release notes.", { n: unseen.length - 8 })}
        </p>
      )}
    </Modal>
  );
}
